import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/use-toast';
import { Turnstile } from '@marsidev/react-turnstile';
import { env, isCaptchaDisabled } from '../../config/env';

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // 🔧 OAUTH CANCELLATION DETECTION: Handle OAuth cancellation on sign-up page
  useEffect(() => {
    const handleOAuthResponse = async () => {
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');

      // Check for OAuth cancellation parameters first
      if (error === 'access_denied' || 
          (error && errorDescription && errorDescription.toLowerCase().includes('cancel'))) {
        
        console.log('🚫 OAuth cancellation detected on signup page:', {
          error,
          errorDescription,
          message: 'User cancelled Google sign-up, staying on signup page'
        });

        // Show informational toast about cancellation
        toast({
          title: "Google sign-up cancelled",
          description: "You can try again with Google or continue with email sign-up below.",
          variant: "default",
        });

        // Clean up URL parameters without redirecting (since user is already on signup page)
        const url = new URL(window.location.href);
        url.search = ''; // Remove all search parameters
        window.history.replaceState({}, document.title, url.pathname);
        return;
      }

      // Note: OAuth success now redirects directly to Dashboard
      // OAuth success detection has been moved to Dashboard.tsx
    };

    handleOAuthResponse();
  }, [searchParams, toast, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 🎯 HACKATHON FEATURE: Skip CAPTCHA validation if disabled
    if (!isCaptchaDisabled() && !turnstileToken) {
      toast({
        title: "Security verification required",
        description: "Please complete the security check to continue.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);

    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // 🎯 HACKATHON FEATURE: Only include captchaToken if CAPTCHA is enabled
          ...(isCaptchaDisabled() ? {} : { captchaToken: turnstileToken || undefined }),
          // Redirect to login page after email verification
          emailRedirectTo: `${window.location.origin}/auth/login`,
        },
      });

      if (signUpError) throw signUpError;

      // Check if email confirmation is required
      if (authData.user && !authData.user.email_confirmed_at) {
        // Email confirmation required
        toast({
          title: "Check your email!",
          description: "We've sent you a confirmation link. Please check your email and click the link to activate your account.",
          duration: 30000, // 30 seconds
        });
        
        // Don't navigate to dashboard - user needs to confirm email first
        setIsLoading(false);
        return;
      }

      // If email is already confirmed (shouldn't happen with new signups, but just in case)
      toast({
        title: "Account created successfully!",
        description: "Welcome to Nosh. Let's get started!",
      });

      // Navigate to dashboard
      navigate('/dashboard');

    } catch (error: any) {
      // Check if it's a rate limit error
      if (error.message?.includes('over_email_send_rate_limit')) {
        const waitTime = 10; // Cooldown time in seconds
        setCooldownTime(waitTime);
        
        toast({
          title: "Please wait a moment",
          description: `For security purposes, please wait ${waitTime} seconds before trying again.`,
          variant: "default",
        });

        // Start countdown
        const interval = setInterval(() => {
          setCooldownTime((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setIsLoading(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Don't set isLoading to false immediately
        return;
      }

      // Handle other errors
      toast({
        title: "Error creating account",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;

    } catch (error: any) {
      toast({
        title: "Error signing up with Google",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div 
      className="w-screen bg-background relative overflow-hidden flex items-center justify-center" 
      style={{ 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Background gradient effect - using consistent app background */}
      <div 
        className="fixed inset-0 bg-background"
      />
      
      {/* Subtle decorative elements */}
      <div 
        className="fixed top-0 right-0 w-96 h-96 rounded-full blur-[100px] opacity-20"
        style={{
          background: 'radial-gradient(circle, hsla(342, 90%, 85%, 0.4), transparent)'
        }}
      />
      
      <div 
        className="fixed bottom-0 left-0 w-80 h-80 rounded-full blur-[120px] opacity-15"
        style={{
          background: 'radial-gradient(circle, hsla(342, 70%, 88%, 0.3), transparent)'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-sm px-4 relative z-10"
      >
        <div className="relative">
          <div className="relative group">
            {/* Card glow effect */}
            <motion.div 
              className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"
              animate={{
                boxShadow: [
                  "0 0 10px 2px rgba(255,255,255,0.03)",
                  "0 0 15px 5px rgba(255,255,255,0.05)",
                  "0 0 10px 2px rgba(255,255,255,0.03)"
                ],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut", 
                repeatType: "mirror" 
              }}
            />

            {/* White card background - matching app design */}
            <div className="relative bg-card backdrop-blur-xl rounded-2xl p-6 border border-border shadow-2xl overflow-hidden">
              {/* Logo and header */}
              <div className="text-center space-y-2 mb-6">
                <a href="https://noshhq.com" className="inline-block">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="mx-auto w-10 h-10 rounded-full border border-primary/20 bg-primary/5 flex items-center justify-center relative overflow-hidden"
                  >
                    <span className="text-lg font-bold text-primary">N</span>
                  </motion.div>
                </a>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl font-bold text-foreground"
                >
                  Create Account
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-muted-foreground text-xs"
                >
                  Join Nosh and start reducing food waste
                </motion.p>
              </div>

              {/* Sign up form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Google Sign Up */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignUp}
                  className="w-full bg-background border-border hover:bg-muted text-foreground h-10"
                >
                  <img 
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                    alt="Google" 
                    className="w-4 h-4 mr-2"
                  />
                  Sign up with Google
                </Button>

                {/* Divider */}
                <div className="relative mt-6 mb-4 flex items-center">
                  <div className="flex-grow border-t border-border"></div>
                  <span className="mx-3 text-xs text-muted-foreground">or</span>
                  <div className="flex-grow border-t border-border"></div>
                </div>

                <motion.div className="space-y-3">
                  {/* Email input */}
                  <div 
                    className={`relative ${focusedInput === "email" ? 'z-10' : ''}`}
                  >
                    <div className="relative flex items-center overflow-hidden rounded-lg">
                      <Mail className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                        focusedInput === "email" ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedInput("email")}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-3 focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>

                  {/* Password input */}
                  <div 
                    className={`relative ${focusedInput === "password" ? 'z-10' : ''}`}
                  >
                    <div className="relative flex items-center overflow-hidden rounded-lg">
                      <Lock className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                        focusedInput === "password" ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedInput("password")}
                        onBlur={() => setFocusedInput(null)}
                        className="w-full bg-input border-border focus:border-primary text-foreground placeholder:text-muted-foreground h-10 transition-all duration-300 pl-10 pr-10 focus:ring-2 focus:ring-primary/20"
                      />
                      
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)} 
                        className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </motion.div>

                {/* 🎯 HACKATHON FEATURE: Conditionally render Turnstile Captcha */}
                {!isCaptchaDisabled() && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-center mt-4"
                  >
                    <Turnstile
                      siteKey={env.TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onError={() => {
                        setTurnstileToken(null);
                        toast({
                          title: "Security verification failed",
                          description: "Please try again or refresh the page.",
                          variant: "destructive",
                        });
                      }}
                      onExpire={() => setTurnstileToken(null)}
                      options={{
                        theme: 'light'
                      }}
                    />
                  </motion.div>
                )}



                {/* Sign up button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-10 mt-5"
                >
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-2"
                      >
                        <div className="w-4 h-4 border-2 border-primary-foreground/70 border-t-transparent rounded-full animate-spin" />
                        {cooldownTime > 0 && (
                          <span className="text-sm">Wait {cooldownTime}s</span>
                        )}
                      </motion.div>
                    ) : (
                      <motion.span
                        key="button-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-2"
                      >
                        Create Account
                        <ArrowRight className="w-4 h-4" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>



                {/* Sign in link */}
                <motion.p 
                  className="text-center text-xs text-muted-foreground mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Already have an account?{' '}
                  <Link 
                    to="/auth/login" 
                    className="text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Sign in
                  </Link>
                </motion.p>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SignUp;